import type { ProductTaxonomy } from '@/services/product-taxonomy';

/**
 * A single selectable event. `value` is the wire value the API stores and matches on, so it must
 * never drift; `label` and `description` are display-only.
 */
export type BaseEvent = {
  label: string;
  value: string;
  description: string;
};

/**
 * A channel's events, grouped the way the event pickers render them. The group `description`
 * describes the effect of selecting the whole group.
 */
export type BaseEventGroup = {
  label: string;
  description: string;
  items: BaseEvent[];
};

/**
 * Descriptions address the audience by the workspace's own noun ("person" vs "profile") and the
 * pronoun that agrees with it. Callers pass the taxonomy in rather than the catalog reading it, so
 * this stays a plain module. Omitting it yields the pre-rename wording.
 */
type BaseEventsOptions = {
  productTaxonomy?: ProductTaxonomy;
};

export function getCustomerEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  return Object.freeze({
    label: 'Customer events',
    description: 'Send all customer subscribes and unsubscribes',
    items: [
      {
        label: 'Subscribed',
        value: 'customer_subscribed',
        description: `A ${personSingular}'s subscription status has changed to subscribed.`,
      },
      {
        label: 'Unsubscribed',
        value: 'customer_unsubscribed',
        description: `A ${personSingular}'s subscription status has changed to unsubscribed.`,
      },
      {
        label: 'Subscription preferences changed',
        value: 'customer_subscription_preferences_changed',
        description: `A ${personSingular}'s subscription preferences have changed.`,
      },
    ],
  });
}
export const customerEvents = getCustomerEvents();
export function getEmailEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  const relativePronoun = options.productTaxonomy?.t('people.relativePronoun') ?? 'who';
  return Object.freeze({
    label: 'Email events',
    description: 'Send all Email events',
    items: [
      {
        label: 'Drafted',
        value: 'email_drafted',
        description: 'An email draft was created.',
      },
      {
        label: 'Attempted',
        value: 'email_attempted',
        description: "An email that couldn't be sent to the delivery provider will be retried.",
      },
      {
        label: 'Deferred',
        value: 'email_deferred',
        description: "An email that the delivery provider couldn't send will be retried by the delivery provider.",
      },
      {
        label: 'Sent',
        value: 'email_sent',
        description: 'An email was sent from Customer.io to the delivery provider.',
      },
      {
        label: 'Delivered',
        value: 'email_delivered',
        description: 'The delivery provider reported the email was delivered to an inbox.',
      },
      {
        label: 'Opened',
        value: 'email_opened',
        description: 'An email was opened.',
      },
      {
        label: 'Clicked',
        value: 'email_clicked',
        description: 'A tracked link in an email was clicked.',
      },
      {
        label: 'Converted',
        value: 'email_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to an email.`,
      },
      {
        label: 'Unsubscribed',
        value: 'email_unsubscribed',
        description: `A ${personSingular} unsubscribed via a particular email.`,
      },
      {
        label: 'Bounced',
        value: 'email_bounced',
        description: 'The delivery provider was unable to deliver the email.',
      },
      {
        label: 'Suppressed',
        value: 'email_dropped',
        description: `An email wasn't sent because it was addressed to a ${personSingular} ${relativePronoun} was suppressed.`,
      },
      {
        label: 'Spammed',
        value: 'email_spammed',
        description: 'An email was marked as spam by the recipient.',
      },
      {
        label: 'Failed',
        value: 'email_failed',
        description: "An email couldn't be sent to the delivery provider.",
      },
      {
        label: 'Undeliverable',
        value: 'email_undeliverable',
        description: 'An email was marked as undeliverable.',
      },
    ],
  });
}
export const emailEvents = getEmailEvents();
export function getInAppEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  return Object.freeze({
    label: 'In-app events',
    description: 'Send all in-app events',
    items: [
      {
        label: 'Drafted',
        value: 'in_app_drafted',
        description: 'An in-app message draft was created.',
      },
      {
        label: 'Attempted',
        value: 'in_app_attempted',
        description: "An in-app message that couldn't be sent will be retried.",
      },
      {
        label: 'Sent',
        value: 'in_app_sent',
        description: `An in-app message was sent from Customer.io and is ready for the ${personSingular} to view on their next visit.`,
      },
      {
        label: 'Opened',
        value: 'in_app_opened',
        description: 'An in-app message was viewed.',
      },
      {
        label: 'Clicked',
        value: 'in_app_clicked',
        description: 'A tracked action in an in-app message was clicked.',
      },
      {
        label: 'Converted',
        value: 'in_app_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to an in-app message.`,
      },
      {
        label: 'Failed',
        value: 'in_app_failed',
        description: 'An in-app message failed to send.',
      },
      {
        label: 'Undeliverable',
        value: 'in_app_undeliverable',
        description: 'An in-app message was marked as undeliverable.',
      },
    ],
  });
}
export const inAppEvents = getInAppEvents();
export function getPushEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  return Object.freeze({
    label: 'Push events',
    description: 'Send all Push events',
    items: [
      {
        label: 'Drafted',
        value: 'push_drafted',
        description: 'A push notification draft was created.',
      },
      {
        label: 'Attempted',
        value: 'push_attempted',
        description: "A push notification that couldn't be sent to the delivery provider will be retried.",
      },
      {
        label: 'Sent',
        value: 'push_sent',
        description: 'A push notification was sent from Customer.io to the delivery provider.',
      },
      {
        label: 'Delivered',
        value: 'push_delivered',
        description: `The app on a ${personSingular}'s device reported the push notification was delivered.`,
      },
      {
        label: 'Opened',
        value: 'push_opened',
        description: `The app on a ${personSingular}'s device reported the push notification was opened.`,
      },
      {
        label: 'Clicked',
        value: 'push_clicked',
        description: 'A tracked link in a push notification has been clicked.',
      },
      {
        label: 'Converted',
        value: 'push_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to a push notification.`,
      },
      {
        label: 'Bounced',
        value: 'push_bounced',
        description: 'The delivery provider reported at least one invalid device token.',
      },
      {
        label: 'Suppressed',
        value: 'push_dropped',
        description: "A push notification wasn't sent because at least one device token previously bounced.",
      },
      {
        label: 'Failed',
        value: 'push_failed',
        description: "A push notification couldn't be sent to the delivery provider.",
      },
      {
        label: 'Undeliverable',
        value: 'push_undeliverable',
        description: 'A push notification was marked as undeliverable.',
      },
    ],
  });
}
export const pushEvents = getPushEvents();
export function getSmsEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  const relativePronoun = options.productTaxonomy?.t('people.relativePronoun') ?? 'who';
  return Object.freeze({
    label: 'SMS events',
    description: 'Send all SMS events',
    items: [
      {
        label: 'Drafted',
        value: 'sms_drafted',
        description: 'An SMS draft was created.',
      },
      {
        label: 'Attempted',
        value: 'sms_attempted',
        description: "An SMS that couldn't be sent to the delivery provider will be retried.",
      },
      {
        label: 'Sent',
        value: 'sms_sent',
        description: 'An SMS was sent from Customer.io to the delivery provider.',
      },
      {
        label: 'Delivered',
        value: 'sms_delivered',
        description: 'The delivery provider reported the SMS was delivered.',
      },
      {
        label: 'Clicked',
        value: 'sms_clicked',
        description: 'A tracked link in an SMS has been clicked.',
      },
      {
        label: 'Converted',
        value: 'sms_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to an SMS.`,
      },
      {
        label: 'Bounced',
        value: 'sms_bounced',
        description: 'The delivery provider was unable to deliver the SMS.',
      },
      {
        label: 'Suppressed',
        value: 'sms_dropped',
        description: `An SMS wasn't sent because it was addressed to a ${personSingular} ${relativePronoun} opted out of SMS from this sender.`,
      },
      {
        label: 'Failed',
        value: 'sms_failed',
        description: "An SMS couldn't be sent to the delivery provider.",
      },
      {
        label: 'Undeliverable',
        value: 'sms_undeliverable',
        description: 'An SMS was marked as undeliverable.',
      },
      {
        label: 'Replied',
        value: 'sms_replied',
        description: `A ${personSingular} replied to an SMS.`,
      },
    ],
  });
}
export const smsEvents = getSmsEvents();
export function getWhatsappEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  const relativePronoun = options.productTaxonomy?.t('people.relativePronoun') ?? 'who';
  return Object.freeze({
    label: 'WhatsApp events',
    description: 'Send all WhatsApp events',
    items: [
      {
        label: 'Drafted',
        value: 'whatsapp_drafted',
        description: 'A WhatsApp message draft was created.',
      },
      {
        label: 'Attempted',
        value: 'whatsapp_attempted',
        description: "A WhatsApp message that couldn't be sent to the delivery provider will be retried.",
      },
      {
        label: 'Sent',
        value: 'whatsapp_sent',
        description: 'A WhatsApp message was sent from Customer.io to the delivery provider.',
      },
      {
        label: 'Delivered',
        value: 'whatsapp_delivered',
        description: 'The delivery provider reported the WhatsApp message was delivered.',
      },
      {
        label: 'Opened',
        value: 'whatsapp_opened',
        description: 'A WhatsApp message was opened.',
      },
      {
        label: 'Clicked',
        value: 'whatsapp_clicked',
        description: 'A tracked link in a WhatsApp message has been clicked.',
      },
      {
        label: 'Converted',
        value: 'whatsapp_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to a WhatsApp message.`,
      },
      {
        label: 'Bounced',
        value: 'whatsapp_bounced',
        description: 'The delivery provider was unable to deliver the WhatsApp message.',
      },
      {
        label: 'Suppressed',
        value: 'whatsapp_dropped',
        description: `A WhatsApp message wasn't sent because it was addressed to a ${personSingular} ${relativePronoun} opted out of WhatsApp from this sender.`,
      },
      {
        label: 'Failed',
        value: 'whatsapp_failed',
        description: "A WhatsApp message couldn't be sent to the delivery provider.",
      },
      {
        label: 'Undeliverable',
        value: 'whatsapp_undeliverable',
        description: 'A WhatsApp message was marked as undeliverable.',
      },
      {
        label: 'Replied',
        value: 'whatsapp_replied',
        description: `A ${personSingular} replied to a WhatsApp message.`,
      },
    ],
  });
}
export const whatsappEvents = getWhatsappEvents();
export const slackEvents: Readonly<BaseEventGroup> = Object.freeze({
  label: 'Slack events',
  description: 'Send all Slack events',
  items: [
    {
      label: 'Drafted',
      value: 'slack_drafted',
      description: 'A slack draft was created.',
    },
    {
      label: 'Attempted',
      value: 'slack_attempted',
      description: "A slack message that couldn't be sent will be retried.",
    },
    {
      label: 'Sent',
      value: 'slack_sent',
      description: 'A slack message was sent from Customer.io to slack.',
    },
    {
      label: 'Clicked',
      value: 'slack_clicked',
      description: 'A tracked link in a slack message has been clicked.',
    },
    {
      label: 'Failed',
      value: 'slack_failed',
      description: "A slack message couldn't be sent to slack.",
    },
  ],
});
export function getWebhookEvents(options: BaseEventsOptions = {}): Readonly<BaseEventGroup> {
  const personSingular = options.productTaxonomy?.term('people.singular') ?? 'person';
  return Object.freeze({
    label: 'Webhook events',
    description: 'Send all Webhook events',
    items: [
      {
        label: 'Drafted',
        value: 'webhook_drafted',
        description: 'A webhook draft was created.',
      },
      {
        label: 'Attempted',
        value: 'webhook_attempted',
        description: "A webhook that couldn't be sent will be retried.",
      },
      {
        label: 'Sent',
        value: 'webhook_sent',
        description: 'A webhook was sent from Customer.io to the specified Webhook URL.',
      },
      {
        label: 'Clicked',
        value: 'webhook_clicked',
        description: 'A tracked link in a webhook payload has been opened.',
      },
      {
        label: 'Converted',
        value: 'webhook_converted',
        description: `A ${personSingular} matched a conversion criteria attributed to a Webhook.`,
      },
      {
        label: 'Failed',
        value: 'webhook_failed',
        description: "A webhook couldn't be sent to the specified Webhook URL.",
      },
      {
        label: 'Undeliverable',
        value: 'webhook_undeliverable',
        description: 'A webhook was marked as undeliverable.',
      },
    ],
  });
}
export const webhookEvents = getWebhookEvents();

export function getBaseEventGroups(options: BaseEventsOptions = {}): readonly Readonly<BaseEventGroup>[] {
  return Object.freeze([
    getCustomerEvents(options),
    getEmailEvents(options),
    getInAppEvents(options),
    getPushEvents(options),
    getSmsEvents(options),
    getWhatsappEvents(options),
    slackEvents,
    getWebhookEvents(options),
  ]);
}

const BaseEventGroups = getBaseEventGroups();

// Two other modules narrow this catalog, and they are not interchangeable:
//
// - `utils/data-out-events.ts` — the native data-out connectors
//   (`/integrations/data_out/:type`). Derived from this catalog through an
//   explicit allowlist.
// - `pages/workspaces/pipelines/sources/[source-id]/utils/message-metric-source-events.ts`
//   — the Pipelines source metric list. It is a standalone copy taken from an
//   older revision of the upstream catalog, so it lags this one (no WhatsApp
//   group; no deferred/undeliverable/dropped/replied events). Treat its
//   narrower set as staleness, not intent, if you ever reconcile the two.

export default BaseEventGroups;
